/**
 * simEvidenceExhibit — the FROZEN payload contract for the "Simulation &
 * Resilience" report appendix section (wave R3, R3-C1), plus the pure
 * governance + projection helpers every render surface (view / PDF / DOCX /
 * PPTX) shares.
 *
 * ── The union (frozen; C2/C3/C4/B2 produce these) ───────────────────────────
 *   - reliability-nplus1   (C2, RELIABILITY_ENGINE, exploratory) — N/N+1/N+2
 *                          availability + nines + EUE table.
 *   - scenario-ridethrough (C3, TRANSIENT_SOLVE, exploratory)     — inlet-T-vs-
 *                          time transient + minutes-to-SLA KPIs + narrative.
 *   - study-*              (C4/B2, UNVALIDATED|VALIDATED_TOOLING) — sweep2d /
 *                          pareto / montecarlo / optimizer study payloads.
 *
 * ── CITATION GOVERNANCE (the load-bearing invariant) ────────────────────────
 * Whether an exhibit renders as a CITED figure is decided by the PRODUCT
 * citation policy (`src/lib/sim/citation/productCitationPolicy.ts`, release
 * 2.1): an exhibit is citable exactly when its persisted source record is
 * tenant-owned, server-verified and release-bound. The decision is stamped at
 * WRITE time (`assembleSimEvidenceExhibits` → `citable` + `citationPolicyId`)
 * and honoured at READ time only when the stored exhibit carries that policy
 * id. Reports re-render from stored docs, so historical exhibits are never
 * rewritten: an exhibit without the stamp — including a legacy payload that
 * mis-set `citable:true` — stays non-citable and renders under the
 * `B7_EXPLORATORY_CAPTION` watermark, and MUST NEVER be folded into the report
 * verdict / Go-No-Go. `isExhibitCitable` and `resolveSimEvidenceExhibits`
 * enforce this defensively so a malformed upstream payload cannot cite itself.
 *
 * Pure / synchronous / null-safe: safe to inline into render bodies, the DOCX/
 * PPTX serializers, and the server orchestration seam alike.
 */
import type { SiteRagReport } from '../../../types/siteRag';
import {
  PRODUCT_CITATION_POLICY_ID,
  productCitationDisposition,
  type ProductCitationFacts,
  type ProductCitationPolicyId,
} from '../../sim/citation/productCitationPolicy';

// ── Reliability (C2) ────────────────────────────────────────────────────────

/** A redundancy-configuration label, e.g. 'N', 'N+1', 'N+2'. Free-form so the
 *  engine can emit richer labels ('N+1 (2N feeders)') without a schema bump. */
export type ReliabilityConfigLevel = string;

/** One redundancy configuration's reliability figures — a row of the
 *  N/N+1/N+2 availability table. */
export interface ReliabilityConfigRow {
  /** Redundancy configuration label ('N', 'N+1', 'N+2', …). */
  config: ReliabilityConfigLevel;
  /** Availability as a fraction in [0,1] (e.g. 0.99995). */
  availability: number;
  /** Equivalent "number of nines" (e.g. 4.3). */
  nines: number;
  /** Equivalent Availability Factor, percent (0..100). */
  eafPct: number;
  /** Expected Unserved Energy, MWh/yr. */
  expectedUnservedEnergyMWhPerYr: number;
  /** Δ availability vs the N baseline, in percentage points (optional). */
  deltaVsBaseline?: number;
}

/**
 * Product citation stamp shared by every exhibit kind. `citable` is the
 * product-policy decision taken at write time; it is honoured at read time
 * only when `citationPolicyId` names the product policy. Exhibits without the
 * stamp (pre-policy documents) are always non-citable.
 */
export interface SimEvidenceExhibitCitationStamp {
  /** Product citation decision for this exhibit's persisted source record. */
  citable: boolean;
  /** Identifier of the policy that took the `citable` decision. Absent on pre-policy exhibits. */
  citationPolicyId?: ProductCitationPolicyId;
}

export interface ReliabilityNplus1Exhibit extends SimEvidenceExhibitCitationStamp {
  kind: 'reliability-nplus1';
  validationStatus: 'RELIABILITY_ENGINE';
  /** Headline / recommended configuration (e.g. 'N+1'). */
  config: ReliabilityConfigLevel;
  /** Headline availability (fraction 0..1) for `config`. */
  availability: number;
  nines: number;
  eafPct: number;
  expectedUnservedEnergyMWhPerYr: number;
  /** Δ availability (percentage points) of `config` vs the N baseline. */
  deltaVsBaseline?: number;
  /** Full per-configuration comparison rows (N / N+1 / N+2 …). When absent the
   *  table shows only the headline row derived from the scalar fields above. */
  rows?: ReliabilityConfigRow[];
  /** Provenance citation strings (engine + run ids). */
  sources: string[];
  /** Optional display title. */
  title?: string;
}

// ── Ride-through (C3) ───────────────────────────────────────────────────────

export interface RideThroughTimelinePoint {
  /** Time since the initiating event, seconds. */
  t: number;
  /** Cooling-loop inlet temperature, °C. */
  inletT_C: number;
  /** Coolant mass flow, kg/s. */
  flow_kgps: number;
  /** Optional event marker at this instant (e.g. 'grid loss', 'genset online'). */
  event?: string;
}

export interface RideThroughKpis {
  /** Seconds until the WARN threshold is crossed. */
  timeToWarn_s: number;
  /** Seconds until the CRITICAL / SLA-breach threshold is crossed. */
  timeToCritical_s: number;
  /** Peak inlet temperature reached, °C. */
  peakInletT_C: number;
  /** SLA inlet-temperature limit, °C. */
  slaLimit_C: number;
}

export interface ScenarioRideThroughExhibit extends SimEvidenceExhibitCitationStamp {
  kind: 'scenario-ridethrough';
  validationStatus: 'TRANSIENT_SOLVE';
  /** Scenario label, e.g. 'Grid loss at 45 °C ambient'. */
  scenario: string;
  timeline: RideThroughTimelinePoint[];
  kpis: RideThroughKpis;
  /** Narrative describing the transient + SLA outcome. */
  narrative: string;
  title?: string;
  /** Optional provenance citation strings (engine + run ids). */
  sources?: string[];
}

// ── Studies (C4 / B2) ───────────────────────────────────────────────────────

export type StudyExhibitKind =
  | 'study-sweep2d'
  | 'study-pareto'
  | 'study-montecarlo'
  | 'study-optimizer';

export interface StudyExhibit extends SimEvidenceExhibitCitationStamp {
  kind: StudyExhibitKind;
  /** Legacy B7 tooling label carried for audit; it is not the citation decision. */
  validationStatus: 'UNVALIDATED_TOOLING' | 'VALIDATED_TOOLING';
  citationId?: string;
  /** Kind-specific payload; readers extract known keys defensively. */
  data: Record<string, unknown>;
  footnote?: string;
  title?: string;
}

export type SimEvidenceExhibit =
  | ReliabilityNplus1Exhibit
  | ScenarioRideThroughExhibit
  | StudyExhibit;

// ── Governance ──────────────────────────────────────────────────────────────

/** The exploratory watermark caption for a non-citable (B7-gated) exhibit.
 *  House style: en-dash, not em-dash (the render/serializer sanitizers key on
 *  U+2014). */
export const B7_EXPLORATORY_CAPTION =
  'Exploratory – not a validated/cited result (B7)';

/** True when the exhibit carries the product citation policy stamp. */
export function hasProductCitationStamp(exhibit: SimEvidenceExhibit): boolean {
  return exhibit.citationPolicyId === PRODUCT_CITATION_POLICY_ID;
}

/**
 * True when an exhibit renders as a CITED figure: it carries the product
 * citation policy stamp AND that stamp's decision is exactly `citable: true`.
 * Anything else — a pre-policy exhibit, a legacy payload that mis-set
 * `citable:true` without the stamp, a stamp from an unknown policy, or a
 * stamped non-citable decision — renders under the B7 watermark.
 */
export function isExhibitCitable(exhibit: SimEvidenceExhibit): boolean {
  return hasProductCitationStamp(exhibit) && exhibit.citable === true;
}

/** The subset of exhibits that are citable results (verdict-eligible). Exposed
 *  so a verdict/Go-No-Go consumer can pull ONLY citable evidence and can never
 *  accidentally fold an exploratory exhibit in. */
export function citableExhibits(
  exhibits: readonly SimEvidenceExhibit[],
): SimEvidenceExhibit[] {
  return exhibits.filter(isExhibitCitable);
}

/**
 * Stamp the product citation decision onto an exhibit at WRITE time. The
 * facts must come from fail-closed SERVER verification of the exhibit's
 * persisted source record (never from the solver's wire flags, never inferred
 * in the browser). Both outcomes are stamped so a stored non-citable exhibit
 * is visibly a policy decision rather than a pre-policy document.
 */
export function stampExhibitCitation<E extends SimEvidenceExhibit>(
  exhibit: E,
  facts: ProductCitationFacts,
): E {
  const disposition = productCitationDisposition(facts);
  return {
    ...exhibit,
    citable: disposition.citable,
    citationPolicyId: disposition.policyId,
  };
}

// ── Orchestration (M3) — pure + dormant-safe ────────────────────────────────

/** One exhibit plus the server-established facts about its persisted source record. */
export interface SimEvidenceExhibitSource<E extends SimEvidenceExhibit = SimEvidenceExhibit> {
  exhibit: E;
  /** Facts from fail-closed server verification of the record the exhibit was built from. */
  citation: ProductCitationFacts;
}

export interface SimEvidenceExhibitInputs {
  /** C2 reliability exhibit. null/absent when the loader isn't merged or found
   *  no reliability run. */
  reliability?: SimEvidenceExhibitSource<ReliabilityNplus1Exhibit> | null;
  /** C3 ride-through exhibit. */
  rideThrough?: SimEvidenceExhibitSource<ScenarioRideThroughExhibit> | null;
  /** Study exhibits built from persisted study records. */
  studyExhibits?: SimEvidenceExhibitSource<StudyExhibit>[] | null;
  /** Extension point for pre-built exhibits (e.g. future engines). */
  extra?: SimEvidenceExhibitSource[] | null;
}

/**
 * Assemble the ordered `SimEvidenceExhibit[]` for the Simulation & Resilience
 * appendix section. Pure + dormant-safe: absent inputs ⇒ `[]` (the section is
 * then absent — no empty section). Order: reliability, ride-through, then the
 * study exhibits. Every exhibit is stamped with the product citation decision
 * for its source record, so the stored document carries both the decision and
 * the policy that took it; an upstream `citable` value is never trusted.
 */
export function assembleSimEvidenceExhibits(
  inputs: SimEvidenceExhibitInputs,
): SimEvidenceExhibit[] {
  const out: SimEvidenceExhibit[] = [];
  const stamp = (source: SimEvidenceExhibitSource): void => {
    out.push(stampExhibitCitation(source.exhibit, source.citation));
  };
  if (inputs.reliability) stamp(inputs.reliability);
  if (inputs.rideThrough) stamp(inputs.rideThrough);
  for (const s of inputs.studyExhibits ?? []) stamp(s);
  for (const e of inputs.extra ?? []) stamp(e);
  return out;
}

function isStudyExhibit(e: SimEvidenceExhibit): e is StudyExhibit {
  return e.kind.startsWith('study-');
}

/**
 * READ-time governance for a stored exhibit. Honour the stored `citable`
 * decision only when the exhibit carries the product policy stamp; otherwise
 * (pre-policy document, mis-flagged legacy payload, unknown policy id) force
 * `citable:false` and drop the foreign stamp so the normalized shape is
 * unambiguous: stamped ⇔ product policy decided.
 */
function normalizeExhibitGovernance(exhibit: SimEvidenceExhibit): SimEvidenceExhibit {
  if (hasProductCitationStamp(exhibit)) {
    const citable = exhibit.citable === true;
    return citable === exhibit.citable ? exhibit : { ...exhibit, citable };
  }
  if (exhibit.citable === false && exhibit.citationPolicyId === undefined) return exhibit;
  const { citationPolicyId: _dropped, ...rest } = exhibit;
  void _dropped;
  return { ...rest, citable: false } as SimEvidenceExhibit;
}

/** True when the report carries ≥1 renderable Simulation & Resilience exhibit. */
export function hasSimEvidenceExhibits(
  report: Pick<SiteRagReport, 'simEvidenceExhibits'>,
): boolean {
  return (
    Array.isArray(report.simEvidenceExhibits) &&
    report.simEvidenceExhibits.length > 0
  );
}

/** The report's stored exhibits, governance-normalized at READ time (the
 *  stored `citable` is honoured only under the product policy stamp), or []
 *  when absent. Historical documents are never rewritten. */
export function resolveSimEvidenceExhibits(
  report: Pick<SiteRagReport, 'simEvidenceExhibits'>,
): SimEvidenceExhibit[] {
  const raw = report.simEvidenceExhibits;
  if (!Array.isArray(raw) || raw.length === 0) return [];
  return raw.map(normalizeExhibitGovernance);
}

// ── Display + shared projections (used by all 4 surfaces) ───────────────────

/** Human title for an exhibit (payload `title` wins; else a kind default). */
export function exhibitTitle(exhibit: SimEvidenceExhibit): string {
  switch (exhibit.kind) {
    case 'reliability-nplus1':
      return exhibit.title ?? 'Reliability – N / N+1 / N+2 Availability';
    case 'scenario-ridethrough':
      return exhibit.title ?? `Ride-Through – ${exhibit.scenario}`;
    case 'study-sweep2d':
      return exhibit.title ?? 'Parameter Sweep (2-D)';
    case 'study-pareto':
      return exhibit.title ?? 'Pareto Front (2-objective)';
    case 'study-montecarlo':
      return exhibit.title ?? 'Monte-Carlo Distribution';
    case 'study-optimizer':
      return exhibit.title ?? 'Optimizer Trace';
    default: {
      const _exhaustive: never = exhibit;
      void _exhaustive;
      return 'Simulation Exhibit';
    }
  }
}

/**
 * The provenance / citation line for a CITABLE exhibit, or null. Shared by
 * every surface so the citation text is identical in the view, PDF, DOCX and
 * PPTX. A citable reliability/ride-through exhibit cites its sources; a
 * citable study cites its `citationId`. Non-citable exhibits return null —
 * they carry the B7 watermark instead of a citation.
 */
export function exhibitCitationLine(exhibit: SimEvidenceExhibit): string | null {
  if (!isExhibitCitable(exhibit)) return null;
  switch (exhibit.kind) {
    case 'reliability-nplus1':
      return exhibit.sources.length > 0 ? `Source: ${exhibit.sources.join('; ')}` : null;
    case 'scenario-ridethrough':
      return exhibit.sources && exhibit.sources.length > 0
        ? `Source: ${exhibit.sources.join('; ')}`
        : 'Source: transient dynamic solve';
    default:
      return exhibit.citationId ? `Cited as [${exhibit.citationId}]` : null;
  }
}

/** Availability fraction (0..1) → percent string with enough precision to show
 *  the nines (e.g. 0.99995 → "99.995%"). */
export function formatAvailabilityPct(fraction: number): string {
  if (!Number.isFinite(fraction)) return 'n/a';
  const pct = Math.max(0, Math.min(1, fraction)) * 100;
  // Show up to 5 decimals, trimmed — enough to read 4–5 nines without noise.
  return `${Number(pct.toFixed(5)).toString()}%`;
}

function fmtNum(n: number, digits = 0): string {
  if (!Number.isFinite(n)) return 'n/a';
  return n.toLocaleString('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

// ── Reliability projection ──────────────────────────────────────────────────

export const RELIABILITY_TABLE_COLUMNS = [
  'Configuration',
  'Availability',
  'Nines',
  'EAF %',
  'EUE (MWh/yr)',
] as const;

/** The config rows for the table: the explicit `rows` when present, else a
 *  single headline row synthesised from the scalar fields. */
export function reliabilityConfigRows(
  exhibit: ReliabilityNplus1Exhibit,
): ReliabilityConfigRow[] {
  if (exhibit.rows && exhibit.rows.length > 0) return exhibit.rows;
  return [
    {
      config: exhibit.config,
      availability: exhibit.availability,
      nines: exhibit.nines,
      eafPct: exhibit.eafPct,
      expectedUnservedEnergyMWhPerYr: exhibit.expectedUnservedEnergyMWhPerYr,
      deltaVsBaseline: exhibit.deltaVsBaseline,
    },
  ];
}

/** The reliability table as string-cell rows — identical on every surface. */
export function reliabilityTableRows(
  exhibit: ReliabilityNplus1Exhibit,
): string[][] {
  return reliabilityConfigRows(exhibit).map((r) => [
    r.config,
    formatAvailabilityPct(r.availability),
    Number.isFinite(r.nines) ? r.nines.toFixed(1) : 'n/a',
    Number.isFinite(r.eafPct) ? r.eafPct.toFixed(2) : 'n/a',
    fmtNum(r.expectedUnservedEnergyMWhPerYr, 0),
  ]);
}

// ── Ride-through projection ─────────────────────────────────────────────────

export const RIDE_THROUGH_KPI_COLUMNS = ['Metric', 'Value'] as const;

/** Ride-through KPI callout rows ([Metric, Value]) — shared by all surfaces. */
export function rideThroughKpiRows(
  exhibit: ScenarioRideThroughExhibit,
): string[][] {
  const k = exhibit.kpis;
  const mins = (s: number) =>
    Number.isFinite(s) ? `${(s / 60).toFixed(1)} min (${fmtNum(s, 0)} s)` : 'n/a';
  const margin =
    Number.isFinite(k.peakInletT_C) && Number.isFinite(k.slaLimit_C)
      ? `${(k.slaLimit_C - k.peakInletT_C).toFixed(1)} °C`
      : 'n/a';
  return [
    ['Time to WARN', mins(k.timeToWarn_s)],
    ['Time to CRITICAL (SLA breach)', mins(k.timeToCritical_s)],
    ['Peak inlet temperature', Number.isFinite(k.peakInletT_C) ? `${k.peakInletT_C.toFixed(1)} °C` : 'n/a'],
    ['SLA inlet limit', Number.isFinite(k.slaLimit_C) ? `${k.slaLimit_C.toFixed(1)} °C` : 'n/a'],
    ['Margin to SLA at peak', margin],
  ];
}

/** True when the transient breaches the SLA (peak ≥ limit, or a finite
 *  time-to-critical). Used for the KPI callout tone — NOT for any verdict. */
export function rideThroughBreachesSla(
  exhibit: ScenarioRideThroughExhibit,
): boolean {
  const k = exhibit.kpis;
  if (Number.isFinite(k.peakInletT_C) && Number.isFinite(k.slaLimit_C)) {
    return k.peakInletT_C >= k.slaLimit_C;
  }
  return Number.isFinite(k.timeToCritical_s);
}

// ── Study data readers (defensive extraction from Record<string, unknown>) ──

function asNumber(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}
function asString(v: unknown): string | null {
  return typeof v === 'string' && v.trim() ? v : null;
}
function asNumberArray(v: unknown): number[] | null {
  if (!Array.isArray(v)) return null;
  const out = v.map(asNumber);
  return out.every((n): n is number => n != null) ? out : null;
}

export interface Sweep2dData {
  xLabel: string;
  yLabel: string;
  xValues: number[];
  yValues: number[];
  /** z[yIdx][xIdx] value grid. */
  z: number[][];
  /** failed[yIdx][xIdx] — true = infeasible/failed cell (rendered hatched). */
  failed: boolean[][];
  valueLabel?: string;
}

export function readSweep2d(data: Record<string, unknown>): Sweep2dData | null {
  const xValues = asNumberArray(data.xValues);
  const yValues = asNumberArray(data.yValues);
  const zRaw = data.z;
  if (!xValues || !yValues || !Array.isArray(zRaw)) return null;
  const z: number[][] = zRaw.map((row) => asNumberArray(row) ?? []);
  if (z.length !== yValues.length) return null;
  const failedRaw = Array.isArray(data.failed) ? data.failed : null;
  const failed: boolean[][] = yValues.map((_, yi) => {
    const row = failedRaw?.[yi];
    return xValues.map((__, xi) => (Array.isArray(row) ? row[xi] === true : false));
  });
  return {
    xLabel: asString(data.xLabel) ?? 'x',
    yLabel: asString(data.yLabel) ?? 'y',
    xValues,
    yValues,
    z,
    failed,
    valueLabel: asString(data.valueLabel) ?? undefined,
  };
}

export interface ParetoPoint {
  x: number;
  y: number;
  onFront: boolean;
  label?: string;
}
export interface ParetoData {
  xLabel: string;
  yLabel: string;
  points: ParetoPoint[];
}

export function readPareto(data: Record<string, unknown>): ParetoData | null {
  if (!Array.isArray(data.points)) return null;
  const points: ParetoPoint[] = [];
  for (const p of data.points) {
    if (typeof p !== 'object' || p == null) continue;
    const rec = p as Record<string, unknown>;
    const x = asNumber(rec.x);
    const y = asNumber(rec.y);
    if (x == null || y == null) continue;
    points.push({
      x,
      y,
      onFront: rec.onFront === true,
      label: asString(rec.label) ?? undefined,
    });
  }
  if (points.length === 0) return null;
  return {
    xLabel: asString(data.xLabel) ?? 'Objective 1',
    yLabel: asString(data.yLabel) ?? 'Objective 2',
    points,
  };
}

export interface MonteCarloBin {
  x0: number;
  x1: number;
  count: number;
}
export interface MonteCarloData {
  label: string;
  unit?: string;
  bins: MonteCarloBin[];
  p5: number;
  p50: number;
  p95: number;
}

/** Percentile of a numeric sample (linear interpolation). */
function percentile(sorted: number[], p: number): number {
  if (sorted.length === 0) return NaN;
  const idx = (sorted.length - 1) * p;
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  if (lo === hi) return sorted[lo]!;
  return sorted[lo]! + (sorted[hi]! - sorted[lo]!) * (idx - lo);
}

export function readMonteCarlo(data: Record<string, unknown>): MonteCarloData | null {
  const label = asString(data.label) ?? 'Outcome';
  const unit = asString(data.unit) ?? undefined;
  const explicitBins = Array.isArray(data.bins)
    ? (data.bins as unknown[])
        .map((b) => {
          if (typeof b !== 'object' || b == null) return null;
          const rec = b as Record<string, unknown>;
          const x0 = asNumber(rec.x0);
          const x1 = asNumber(rec.x1);
          const count = asNumber(rec.count);
          return x0 != null && x1 != null && count != null ? { x0, x1, count } : null;
        })
        .filter((b): b is MonteCarloBin => b != null)
    : null;

  const samples = asNumberArray(data.samples);
  let bins = explicitBins ?? [];
  let p5 = asNumber(data.p5);
  let p50 = asNumber(data.p50);
  let p95 = asNumber(data.p95);

  if (samples && samples.length > 0) {
    const sorted = [...samples].sort((a, b) => a - b);
    p5 = p5 ?? percentile(sorted, 0.05);
    p50 = p50 ?? percentile(sorted, 0.5);
    p95 = p95 ?? percentile(sorted, 0.95);
    if (bins.length === 0) {
      const min = sorted[0]!;
      const max = sorted[sorted.length - 1]!;
      const nBins = Math.min(20, Math.max(5, Math.round(Math.sqrt(sorted.length))));
      const width = (max - min) / nBins || 1;
      bins = Array.from({ length: nBins }, (_, i) => {
        const x0 = min + i * width;
        const x1 = i === nBins - 1 ? max : x0 + width;
        const count = sorted.filter((s) => s >= x0 && (i === nBins - 1 ? s <= x1 : s < x1)).length;
        return { x0, x1, count };
      });
    }
  }

  if (bins.length === 0 || p5 == null || p50 == null || p95 == null) return null;
  return { label, unit, bins, p5, p50, p95 };
}

export interface OptimizerData {
  objectiveLabel: string;
  /** Best-point parameter/value pairs. */
  best: { label: string; value: string }[];
  /** Convergence trace (objective vs iteration). */
  trace: { iter: number; value: number }[];
}

export function readOptimizer(data: Record<string, unknown>): OptimizerData | null {
  const objectiveLabel = asString(data.objectiveLabel) ?? 'Objective';
  const best: { label: string; value: string }[] = [];
  if (data.best && typeof data.best === 'object' && !Array.isArray(data.best)) {
    for (const [k, v] of Object.entries(data.best as Record<string, unknown>)) {
      const num = asNumber(v);
      best.push({ label: k, value: num != null ? Number(num.toFixed(4)).toString() : String(v) });
    }
  }
  const trace: { iter: number; value: number }[] = [];
  if (Array.isArray(data.trace)) {
    data.trace.forEach((t, i) => {
      if (typeof t === 'object' && t != null) {
        const rec = t as Record<string, unknown>;
        const value = asNumber(rec.value);
        if (value != null) trace.push({ iter: asNumber(rec.iter) ?? i, value });
      } else {
        const value = asNumber(t);
        if (value != null) trace.push({ iter: i, value });
      }
    });
  }
  if (best.length === 0 && trace.length === 0) return null;
  return { objectiveLabel, best, trace };
}

/**
 * Study summary rows ([Metric, Value]) for the TABULAR surfaces (DOCX / PPTX)
 * and the study fallback table on the view when its chart can't build. Reads
 * `data` defensively per kind so a partial payload still yields a legible
 * table. Never throws.
 */
export function studySummaryRows(exhibit: StudyExhibit): string[][] {
  const rows: string[][] = [];
  switch (exhibit.kind) {
    case 'study-sweep2d': {
      const s = readSweep2d(exhibit.data);
      if (s) {
        rows.push([s.xLabel, `${s.xValues.length} levels`]);
        rows.push([s.yLabel, `${s.yValues.length} levels`]);
        const failedCount = s.failed.reduce(
          (acc, r) => acc + r.filter(Boolean).length,
          0,
        );
        const total = s.xValues.length * s.yValues.length;
        rows.push(['Cells evaluated', `${total}`]);
        rows.push(['Failed / infeasible cells', `${failedCount}`]);
      }
      break;
    }
    case 'study-pareto': {
      const p = readPareto(exhibit.data);
      if (p) {
        rows.push(['Objective 1', p.xLabel]);
        rows.push(['Objective 2', p.yLabel]);
        rows.push(['Candidate points', `${p.points.length}`]);
        rows.push([
          'On Pareto front',
          `${p.points.filter((pt) => pt.onFront).length}`,
        ]);
      }
      break;
    }
    case 'study-montecarlo': {
      const m = readMonteCarlo(exhibit.data);
      if (m) {
        const u = m.unit ? ` ${m.unit}` : '';
        rows.push(['Metric', m.label]);
        rows.push(['P5', `${Number(m.p5.toFixed(3))}${u}`]);
        rows.push(['P50 (median)', `${Number(m.p50.toFixed(3))}${u}`]);
        rows.push(['P95', `${Number(m.p95.toFixed(3))}${u}`]);
      }
      break;
    }
    case 'study-optimizer': {
      const o = readOptimizer(exhibit.data);
      if (o) {
        rows.push(['Objective', o.objectiveLabel]);
        for (const b of o.best) rows.push([`Best: ${b.label}`, b.value]);
        if (o.trace.length > 0) {
          rows.push(['Iterations', `${o.trace.length}`]);
          rows.push([
            'Final objective',
            `${Number(o.trace[o.trace.length - 1]!.value.toFixed(4))}`,
          ]);
        }
      }
      break;
    }
    default: {
      const _exhaustive: never = exhibit.kind;
      void _exhaustive;
    }
  }
  return rows;
}

/**
 * Flatten an exhibit to `[Metric, Value]` rows for the DOCX/PPTX serializers
 * and any tabular fallback. Reliability → the config table; ride-through → the
 * KPI rows; study → `studySummaryRows`. The chart-bearing surfaces (view/PDF)
 * additionally draw the figure; these rows are the surface-agnostic content
 * spine that keeps every export in parity.
 */
export function exhibitTableRows(exhibit: SimEvidenceExhibit): string[][] {
  switch (exhibit.kind) {
    case 'reliability-nplus1':
      return reliabilityTableRows(exhibit);
    case 'scenario-ridethrough':
      return rideThroughKpiRows(exhibit);
    default:
      return studySummaryRows(exhibit);
  }
}

/** Column headers for `exhibitTableRows` (kind-aware). */
export function exhibitTableColumns(exhibit: SimEvidenceExhibit): string[] {
  switch (exhibit.kind) {
    case 'reliability-nplus1':
      return [...RELIABILITY_TABLE_COLUMNS];
    case 'scenario-ridethrough':
      return [...RIDE_THROUGH_KPI_COLUMNS];
    default:
      return ['Metric', 'Value'];
  }
}
